import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ClientDTO } from '../../services/clientApi';
import { Coordinates, MapProvider, getClientCoordinates } from '../../utils/mapUtils';
import { getUserPreferences, subscribeUserPreferences, UserPreferences } from '../../services/userPreferences';

// Fix pour les icônes Leaflet avec Vite (le bundler ne copie pas les assets automatiquement)
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

const defaultIcon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = defaultIcon;

// Icône bleue pour les clients actifs, grise pour les inactifs
const activeIcon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const inactiveMarkerSvg = [
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41" width="25" height="41">',
  '<path fill="#9CA3AF" stroke="#fff" stroke-width="1.5"',
  'd="M12.5 0C5.6 0 0 5.6 0 12.5c0 9.4 12.5 28.5 12.5 28.5S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>',
  '<circle cx="12.5" cy="12.5" r="5" fill="#fff"/>',
  '</svg>',
].join('');

const inactiveIconUrl = `data:image/svg+xml;base64,${btoa(inactiveMarkerSvg)}`;

const inactiveIcon = L.icon({
  iconUrl: inactiveIconUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const OSM_TILE_CONFIG = {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  subdomains: ['a', 'b', 'c'],
  maxZoom: 19,
};

const GOOGLE_TILE_CONFIG = {
  attribution: '&copy; Google',
  url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
  subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
  maxZoom: 20,
};

interface FitBoundsProps {
  points: [number, number][];
}

/** Recentre automatiquement la carte sur tous les marqueurs visibles */
const FitBounds: React.FC<FitBoundsProps> = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    }
  }, [points, map]);

  return null;
};

interface ClientsMapProps {
  clients: ClientDTO[];
  onViewClient: (client: ClientDTO) => void;
  isLoading?: boolean;
}

export const ClientsMap: React.FC<ClientsMapProps> = ({ clients, onViewClient, isLoading }) => {
  const [selectedMapProvider, setSelectedMapProvider] = useState<MapProvider>('google');
  const [activeMapProvider, setActiveMapProvider] = useState<MapProvider>('google');
  const [isMapFallbackEnabled, setIsMapFallbackEnabled] = useState(true);
  const [mapMessage, setMapMessage] = useState<string | null>(null);
  const hasHandledTileErrorRef = useRef(false);

  useEffect(() => {
    const applyPreferences = (preferences: UserPreferences) => {
      setSelectedMapProvider(preferences.mapProvider);
      setActiveMapProvider(preferences.mapProvider);
      setIsMapFallbackEnabled(preferences.enableMapFallback);
      setMapMessage(null);
      hasHandledTileErrorRef.current = false;
    };

    applyPreferences(getUserPreferences());
    const unsubscribe = subscribeUserPreferences(applyPreferences);

    return () => {
      unsubscribe();
    };
  }, []);

  const geocodedClients: Array<{ client: ClientDTO; coordinates: Coordinates }> = clients
    .map((client) => {
      const coordinates = getClientCoordinates(client);
      return coordinates ? { client, coordinates } : null;
    })
    .filter((entry): entry is { client: ClientDTO; coordinates: Coordinates } => entry !== null);

  const mapPoints = geocodedClients.map(({ coordinates }) => [coordinates.lat, coordinates.lng] as [number, number]);
  const missingCount = clients.length - geocodedClients.length;

  const tileConfig = activeMapProvider === 'google' ? GOOGLE_TILE_CONFIG : OSM_TILE_CONFIG;

  const handleTileError = () => {
    if (activeMapProvider !== 'google' || hasHandledTileErrorRef.current) {
      return;
    }

    hasHandledTileErrorRef.current = true;

    if (isMapFallbackEnabled) {
      setActiveMapProvider('osm');
      setMapMessage('Google Maps indisponible, bascule vers OpenStreetMap.');
      return;
    }

    setMapMessage('Google Maps indisponible et fallback desactive dans vos parametres.');
  };

  return (
    <div className="space-y-2">
      {missingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>
            <strong>{missingCount} client{missingCount > 1 ? 's' : ''}</strong> sans coordonnées GPS
            {missingCount > 1 ? ' ne sont pas affichés' : ' n\'est pas affiché'} sur la carte.
            Utilisez le bouton <em>Géolocaliser</em> dans la fiche client pour les ajouter.
          </span>
        </div>
      )}

      <div className="relative bg-white rounded-lg shadow overflow-hidden" style={{ height: '560px' }}>
        <div className="absolute left-3 top-3 z-[900] rounded-md border border-gray-200 bg-white/95 px-2.5 py-1 text-xs text-gray-700 shadow-sm">
          Fond de carte: {activeMapProvider === 'google' ? 'Google Maps' : 'OpenStreetMap'}
          {activeMapProvider !== selectedMapProvider && ' (fallback)'}
        </div>

        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-[1000]">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {geocodedClients.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-3">
            <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-sm">Aucun client géolocalisé à afficher.</p>
          </div>
        ) : (
          <MapContainer
            center={[46.603354, 1.888334]} // Centre de la France
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution={tileConfig.attribution}
              url={tileConfig.url}
              subdomains={tileConfig.subdomains}
              maxZoom={tileConfig.maxZoom}
              eventHandlers={{ tileerror: handleTileError }}
            />
            <FitBounds points={mapPoints} />
            {geocodedClients.map(({ client, coordinates }) => (
              <Marker
                key={client.id}
                position={[coordinates.lat, coordinates.lng]}
                icon={client.isActive === false ? inactiveIcon : activeIcon}
              >
                <Popup minWidth={200}>
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-900 text-sm">
                      {client.firstName} {client.lastName}
                    </p>
                    {client.company && (
                      <p className="text-xs text-gray-600">{client.company}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      {[client.address, client.postalCode, client.city].filter(Boolean).join(', ')}
                    </p>
                    {client.phone && (
                      <p className="text-xs text-gray-500">
                        <a href={`tel:${client.phone}`} className="text-blue-600 hover:underline">
                          {client.phone}
                        </a>
                      </p>
                    )}
                    {client.isActive === false && (
                      <span className="inline-block text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                        Inactif
                      </span>
                    )}
                    <div className="pt-1">
                      <button
                        onClick={() => onViewClient(client)}
                        className="text-xs text-white bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded w-full"
                      >
                        Voir la fiche
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      {mapMessage && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          {mapMessage}
        </div>
      )}

      {geocodedClients.length > 0 && (
        <p className="text-xs text-gray-400 text-right">
          {geocodedClients.length} client{geocodedClients.length > 1 ? 's' : ''} affiché{geocodedClients.length > 1 ? 's' : ''} sur la carte
          {activeMapProvider === 'google' ? (
            <> · Maps © Google</>
          ) : (
            <> · Maps © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" className="underline">OpenStreetMap</a></>
          )}
        </p>
      )}
    </div>
  );
};

export default ClientsMap;
